import {
  authenticateByToken,
  AuthProcess,
  getConfig,
} from "../allauth/lib/allauth";
import { loadNativeWebViewInsets } from "./nativeWebViewInsets";

export const NATIVE_AUTH_MESSAGE_TYPE = "nativeAuth";

export type NativeAuthMessage = {
  type: typeof NATIVE_AUTH_MESSAGE_TYPE;
  provider?: string;
  status: "success" | "error";
  idToken?: string;
  code?: string;
  message?: string;
  process?: string;
};

export type NativeAuthResult = {
  status: "success" | "error";
  code?: string;
  message?: string;
};

export type AuthProcessName = (typeof AuthProcess)[keyof typeof AuthProcess];

type NativeAuthListener = (result: NativeAuthResult) => void;

declare global {
  interface Window {
    flutter_inappwebview?: {
      callHandler: (name: string, ...args: unknown[]) => Promise<unknown>;
    };
  }
}

const listeners = new Set<NativeAuthListener>();
let messageListenerRegistered = false;
let exchangeInFlight = false;

export function isNativeWebView(): boolean {
  return loadNativeWebViewInsets().isNativeWebView;
}

export function subscribeNativeAuth(listener: NativeAuthListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function notifyListeners(result: NativeAuthResult): void {
  listeners.forEach((listener) => listener(result));
}

export function parseNativeAuthMessage(data: unknown): NativeAuthMessage | null {
  let parsed: unknown = data;

  if (typeof data === "string") {
    try {
      parsed = JSON.parse(data);
    } catch {
      return null;
    }
  }

  if (!parsed || typeof parsed !== "object") {
    return null;
  }

  const message = parsed as Record<string, unknown>;

  if (message.type === NATIVE_AUTH_MESSAGE_TYPE) {
    return message as NativeAuthMessage;
  }

  // Legacy Flutter OAUTH_TOKEN envelope
  if (message.type === "OAUTH_TOKEN" && message.data && typeof message.data === "object") {
    const tokenData = message.data as Record<string, unknown>;
    if (typeof tokenData.token === "string") {
      return {
        type: NATIVE_AUTH_MESSAGE_TYPE,
        provider: typeof tokenData.provider === "string" ? tokenData.provider : "google",
        status: "success",
        idToken: tokenData.token,
        process: AuthProcess.LOGIN,
      };
    }
  }

  // Legacy auth-response from early bridge experiments
  if (message.type === "auth-response" && message.provider === "google") {
    if (typeof message.token === "string") {
      return {
        type: NATIVE_AUTH_MESSAGE_TYPE,
        provider: "google",
        status: "success",
        idToken: message.token,
        process: AuthProcess.LOGIN,
      };
    }
  }

  return null;
}

async function resolveGoogleClientId(): Promise<string> {
  const config = await getConfig();
  const provider = config.data?.socialaccount?.providers?.find(
    (entry: { id: string }) => entry.id === "google"
  );
  if (!provider?.client_id) {
    throw new Error("Google provider is not configured");
  }
  return provider.client_id;
}

async function resolveAppleBundleClientId(): Promise<string> {
  const configured = process.env.REACT_APP_APPLE_BUNDLE_ID;
  if (configured) {
    return configured;
  }

  return "com.smsp.myapp";
}

export async function exchangeGoogleIdToken(
  idToken: string,
  authProcess: AuthProcessName = AuthProcess.LOGIN
): Promise<unknown> {
  const clientId = await resolveGoogleClientId();
  return authenticateByToken(
    "google",
    {
      id_token: idToken,
      client_id: clientId,
    },
    authProcess
  );
}

export async function exchangeAppleIdToken(
  idToken: string,
  authProcess: AuthProcessName = AuthProcess.LOGIN
): Promise<unknown> {
  const clientId = await resolveAppleBundleClientId();
  return authenticateByToken(
    "apple",
    {
      id_token: idToken,
      client_id: clientId,
    },
    authProcess
  );
}

async function handleNativeAuthMessage(message: NativeAuthMessage): Promise<void> {
  const authProcess = (message.process || AuthProcess.LOGIN) as AuthProcessName;

  if (message.status === "error") {
    notifyListeners({
      status: "error",
      code: message.code,
      message: message.message,
    });
    return;
  }

  if (!message.idToken) {
    return;
  }

  if (exchangeInFlight) {
    return;
  }

  exchangeInFlight = true;
  try {
    if (message.provider === "google") {
      await exchangeGoogleIdToken(message.idToken, authProcess);
    } else if (message.provider === "apple") {
      await exchangeAppleIdToken(message.idToken, authProcess);
    } else {
      return;
    }
    notifyListeners({ status: "success" });
  } catch (error) {
    const err = error as Error;
    const providerLabel =
      message.provider === "apple" ? "Apple" : "Google";
    notifyListeners({
      status: "error",
      code: "exchange_failed",
      message: err.message || `Failed to complete ${providerLabel} sign-in`,
    });
  } finally {
    exchangeInFlight = false;
  }
}

function onWindowMessage(event: MessageEvent): void {
  const message = parseNativeAuthMessage(event.data);
  if (!message) {
    return;
  }
  void handleNativeAuthMessage(message);
}

export function registerNativeAuthMessageListener(): void {
  if (typeof window === "undefined" || messageListenerRegistered) {
    return;
  }
  window.addEventListener("message", onWindowMessage);
  messageListenerRegistered = true;
}

export async function requestNativeGoogleSignIn(
  authProcess: AuthProcessName = AuthProcess.LOGIN
): Promise<void> {
  if (window.flutter_inappwebview?.callHandler) {
    await window.flutter_inappwebview.callHandler("googleSignIn", authProcess);
    return;
  }

  window.postMessage(
    JSON.stringify({
      type: "auth-request",
      provider: "google",
      process: authProcess,
    }),
    process.env.REACT_APP_APP_HOST || "*"
  );
}

export async function requestNativeAppleSignIn(
  authProcess: AuthProcessName = AuthProcess.LOGIN
): Promise<void> {
  if (window.flutter_inappwebview?.callHandler) {
    await window.flutter_inappwebview.callHandler("appleSignIn", authProcess);
    return;
  }

  window.postMessage(
    JSON.stringify({
      type: "auth-request",
      provider: "apple",
      process: authProcess,
    }),
    process.env.REACT_APP_APP_HOST || "*"
  );
}

export async function requestNativeGoogleSignOut(): Promise<void> {
  if (window.flutter_inappwebview?.callHandler) {
    await window.flutter_inappwebview.callHandler("googleSignOut");
  }
}

export async function requestSilentNativeGoogleSignIn(): Promise<void> {
  if (!isNativeWebView()) {
    return;
  }
  if (window.flutter_inappwebview?.callHandler) {
    await window.flutter_inappwebview.callHandler("attemptSilentGoogleSignIn");
  }
}

export function bootstrapNativeAuthBridge(): void {
  registerNativeAuthMessageListener();
}
